(() => {
  class RetailStackedCards {
    constructor(section) {
      this.section = section;
      this.intro = section.querySelector('.retail-stacked-cards__intro');
      this.mobileTrack = section;
      this.mobileItems = section.querySelector('[data-rsc-mobile-track]');
      this.mobileWraps = Array.from(section.querySelectorAll('[data-rsc-mobile-wrap]'));
      this.mobileCards = Array.from(section.querySelectorAll('[data-rsc-mobile-card]'));
      this.mq = window.matchMedia('(max-width: 749px)');
      this.scrollTrigger = null;
      this.retryTimer = null;
      this.initAttempt = 0;
      this.resizeHandler = this.handleResize.bind(this);
      this.mediaChangeHandler = this.handleResize.bind(this);
      this.loadHandler = this.handleResize.bind(this);
      this.lastViewportWidth = window.innerWidth;

      this.waitForSetup();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.handleResize()).catch(() => {});
      }

      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('load', this.loadHandler, { once: true });
      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this.mediaChangeHandler);
      } else {
        this.mq.addListener(this.mediaChangeHandler);
      }
    }

    waitForSetup() {
      clearTimeout(this.retryTimer);

      if ((window.gsap && window.ScrollTrigger) || this.initAttempt > 20) {
        this.initAttempt = 0;
        this.setup();
        return;
      }

      this.initAttempt += 1;
      this.retryTimer = window.setTimeout(() => this.waitForSetup(), 150);
    }

    setup() {
      this.destroyScrollTrigger();
      this.applyStickyOffsets();

      if (!this.mq.matches || !this.mobileItems || this.mobileCards.length === 0 || !window.gsap || !window.ScrollTrigger) {
        this.resetCards();
        return;
      }

      window.gsap.registerPlugin(window.ScrollTrigger);
      const scrollTriggerConfig = {
        ignoreMobileResize: true
      };

      if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
        scrollTriggerConfig.autoRefreshEvents = 'visibilitychange,DOMContentLoaded,load';
      }

      window.ScrollTrigger.config(scrollTriggerConfig);

      const stickyTop = parseFloat(window.getComputedStyle(this.mobileItems).top) || 0;

      this.scrollTrigger = window.ScrollTrigger.create({
        // The cards container is the sticky element now. Progress starts the
        // moment it pins (its top meets the sticky offset) and runs for exactly
        // the distance it stays pinned — the spare room beneath it inside
        // .page-width — so stacking only happens while it's locked in place.
        trigger: this.mobileItems,
        start: () => `top top+=${stickyTop}`,
        end: () => {
          const container = this.mobileItems.closest('.page-width') || this.mobileItems.parentElement;
          const pinnedDistance = container.offsetHeight - this.mobileItems.offsetTop - this.mobileItems.offsetHeight;
          return `+=${Math.max(1, pinnedDistance)}`;
        },
        scrub: 0.65,
        invalidateOnRefresh: true,
        onUpdate: (self) => this.render(self.progress)
      });

      this.render(0);
      window.ScrollTrigger.refresh();
    }

    applyStickyOffsets() {
      const stickyBaseTop = 28;

      this.section.style.setProperty('--rsc-mobile-sticky-top', `${stickyBaseTop}px`);

      this.mobileWraps.forEach((wrap, index) => {
        wrap.style.top = `${stickyBaseTop}px`;
        wrap.style.zIndex = `${index + 1}`;
      });

      this.mobileCards.forEach((card, index) => {
        card.style.zIndex = `${index + 2}`;
      });
    }

    render(progress) {
      const totalTransitions = Math.max(1, this.mobileCards.length - 1);

      // Split each card's scroll segment into: a lead "rest" where the current
      // card (incl. its CTA) stays fully locked in view, the actual hand-off,
      // then a short tail rest once the next card has fully arrived. Without
      // this dwell the next card begins rising the instant you scroll, before
      // the current card's CTA can be read.
      const lead = 0.4;
      const tail = 0.12;
      const scaled = this.clamp(progress) * totalTransitions;
      const segment = Math.min(Math.floor(scaled), totalTransitions - 1);
      const within = scaled - segment;
      let advance;
      if (within <= lead) {
        advance = 0;
      } else if (within >= 1 - tail) {
        advance = 1;
      } else {
        advance = (within - lead) / (1 - lead - tail);
      }
      const stackPos = segment + advance;

      this.mobileCards.forEach((card, index) => {
        const rel = index - stackPos;
        let translate = '0px';
        let scale = 1;
        let opacity = 1;

        if (rel >= 1) {
          translate = '110%';
          opacity = 0;
        } else if (rel > 0) {
          translate = `${rel * 110}%`;
        } else {
          const depth = -rel;
          translate = `${-(depth * 25)}px`;
          scale = Math.max(0.82, 1 - depth * 0.08);
        }

        card.style.transform = `translate3d(0, ${translate}, 0) scale(${scale})`;
        card.style.opacity = `${opacity}`;
      });
    }

    resetCards() {
      this.mobileCards.forEach((card) => {
        card.style.transform = '';
        card.style.opacity = '';
        card.style.zIndex = '';
      });
    }

    destroyScrollTrigger() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }

    handleResize() {
      const viewportWidth = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      if (isTouchDevice && viewportWidth === this.lastViewportWidth) {
        return;
      }

      this.lastViewportWidth = viewportWidth;
      this.setup();
    }

    clamp(value, min = 0, max = 1) {
      return Math.min(max, Math.max(min, value));
    }

    destroy() {
      clearTimeout(this.retryTimer);
      this.destroyScrollTrigger();
      window.removeEventListener('resize', this.resizeHandler);

      if (this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this.mediaChangeHandler);
      } else {
        this.mq.removeListener(this.mediaChangeHandler);
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-retail-stacked-cards]').forEach((section) => {
      if (section.__retailStackedCards) return;
      section.__retailStackedCards = new RetailStackedCards(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
